/**
 * Nudge scroll rotator (pills rewrite P6) — the trigger that replaces
 * section-scroll's heading matching. The server no longer sends sections;
 * "the page is watching" is now viewport progress through the document in
 * THIRDS: settling into a new third deals the next nudge window (all local,
 * zero network). Same 220ms settle discipline section-scroll used, so a
 * momentum fling doesn't machine-gun rotations.
 */

/** Which third of the document the viewport bottom has reached (0/1/2).
 *  Pure — exported for tests. Degenerate heights clamp to 0. */
export function scrollThird(scrollY: number, viewportH: number, docH: number): number {
  const scrollable = Math.max(1, docH - viewportH);
  const progress = Math.min(1, Math.max(0, scrollY / scrollable));
  return Math.min(2, Math.floor(progress * 3));
}

/** How far into the DOCUMENT the viewport bottom sits (0..1) — the
 *  scroll-ahead comparison value: a section whose fraction is past this is
 *  content the visitor has not seen yet. Pure — exported for tests. */
export function scrollBottomFraction(scrollY: number, viewportH: number, docH: number): number {
  const h = Math.max(1, docH);
  return Math.min(1, Math.max(0, (scrollY + viewportH) / h));
}

export type NudgeScrollHandle = { stop: () => void };

export function startNudgeScroll(opts: {
  onThirdChange: () => void;
  /** Fired on EVERY settle with the viewport-bottom document fraction —
   *  the scroll-ahead section slot reads it (GH #590). */
  onFraction?: (f: number) => void;
  settleMs?: number;
}): NudgeScrollHandle {
  const settleMs = opts.settleMs ?? 220;
  let lastThird = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const evaluate = () => {
    timer = null;
    opts.onFraction?.(
      scrollBottomFraction(window.scrollY, window.innerHeight, document.documentElement.scrollHeight),
    );
    const third = scrollThird(
      window.scrollY,
      window.innerHeight,
      document.documentElement.scrollHeight,
    );
    if (third !== lastThird) {
      lastThird = third;
      opts.onThirdChange();
    }
  };
  const onScroll = () => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(evaluate, settleMs);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  return {
    stop() {
      window.removeEventListener("scroll", onScroll);
      if (timer != null) clearTimeout(timer);
      timer = null;
    },
  };
}
